import { type ForwardedRef, forwardRef, type ReactNode, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { RequestError } from '@subaio/utils'

import { Container } from './Container'
import { context, type SnackbarContext } from './context'
import { SnackbarType } from './SnackbarType'
import { Wrapper } from './Wrapper'

const defaultAnimateOutSeconds = 0.4

export type Snackbar = {
	id: string
	message: string
	type: SnackbarType
	expiration: number
	expired: boolean

	onClose: () => void
}

type Props = {
	render: (snackbar: Snackbar) => React.JSX.Element
	animateOutSeconds?: number
	children: ReactNode
}

const getNextTime = (animateOutSeconds: number, { expiration, expired }: Snackbar) =>
	expired ? expiration + animateOutSeconds * 1000 : expiration

export const SnackbarProvider = forwardRef(
	({ render, animateOutSeconds = defaultAnimateOutSeconds, children }: Props, ref: ForwardedRef<SnackbarContext>) => {
		const [snackbars, setSnackbars] = useState<Array<Snackbar>>([])

		useEffect(() => {
			if (snackbars.length === 0) return

			const nextExpiration = snackbars.reduce((a, b) =>
				getNextTime(animateOutSeconds, a) <= getNextTime(animateOutSeconds, b) ? a : b,
			)

			const timeout = setTimeout(() => {
				if (nextExpiration.expired) {
					setSnackbars((current) => current.filter((snackbar) => snackbar !== nextExpiration))
				} else {
					setSnackbars((current) =>
						current.map((snackbar) => (snackbar === nextExpiration ? { ...snackbar, expired: true } : snackbar)),
					)
				}
			}, getNextTime(animateOutSeconds, nextExpiration) - Date.now())

			return () => {
				clearTimeout(timeout)
			}
		}, [snackbars, animateOutSeconds])

		const contextValue = useMemo<SnackbarContext>(() => {
			const onCloseSnackbar = (id: string) => {
				setSnackbars((current) =>
					current.map((snackbar) =>
						snackbar.id === id ? { ...snackbar, expiration: Date.now(), expired: true } : snackbar,
					),
				)
			}

			const showSnackbar: SnackbarContext['showSnackbar'] = ({ message, duration = 7, type = SnackbarType.Mute }) => {
				const id = `snackbar${Math.random()}`

				setSnackbars((current) => [
					...current,
					{
						id,
						message,
						type,
						expiration: Date.now() + duration * 1000,
						expired: false,
						onClose: () => onCloseSnackbar(id),
					},
				])
			}

			const handleSnackbars: SnackbarContext['handleSnackbars'] =
				(func, handlers?) =>
				async (...args) => {
					try {
						const message = await func(...args)
						if (message) {
							showSnackbar({ message, type: SnackbarType.Success })
						}
					} catch (error) {
						let handler = null
						if (error instanceof RequestError && handlers && error.request.statusCode in handlers) {
							handler = handlers[error.request.statusCode]!
							// If there's a handler for this status code, we shouldn't log anything.
							error.isHandled = true
						} else if (handlers && 'default' in handlers) {
							handler = handlers?.default
						} else {
							handler = 'Unknown error'
						}

						let message = null
						if (typeof handler === 'string') {
							message = handler
						} else if (typeof handler === 'function') {
							const result = await handler(...args)
							message = typeof result === 'string' ? result : null
						}

						if (message) {
							showSnackbar({ message, type: SnackbarType.Error })
						}

						throw error
					}
				}

			return { showSnackbar, handleSnackbars }
		}, [])

		// Expose context as ref
		useImperativeHandle(ref, () => contextValue, [contextValue])

		return (
			<context.Provider value={contextValue}>
				{children}
				<Container>
					{snackbars.map((snackbar) => (
						<Wrapper key={snackbar.id}>{render(snackbar)}</Wrapper>
					))}
				</Container>
			</context.Provider>
		)
	},
)
